import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, PhoneOff, PhoneMissed, Ban, Phone, Loader2, HandCoins } from "lucide-react";

const Choice = ({ icon: Icon, label, hint, onClick, tone = "default", testid }) => {
  const tones = {
    default: "border-slate-200 hover:border-slate-900 hover:bg-slate-50 text-slate-800",
    green: "border-green-200 hover:border-green-500 hover:bg-green-50 text-green-700",
    red: "border-rose-200 hover:border-rose-500 hover:bg-rose-50 text-rose-700",
    amber: "border-amber-200 hover:border-amber-500 hover:bg-amber-50 text-amber-700",
  };
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 border rounded-lg px-4 py-3.5 text-left transition-colors ${tones[tone]}`}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span className="flex-1">
        <span className="block text-sm font-semibold">{label}</span>
        {hint && <span className="block text-xs text-slate-400 font-normal">{hint}</span>}
      </span>
    </button>
  );
};

export default function CollectionAction() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const fromTab = params.get("tab") || "overdue";

  const [loan, setLoan] = useState(null);
  const [step, setStep] = useState("payment"); // payment | amount | contact | outcome | promiseDate
  const [amountStatus, setAmountStatus] = useState("Collected"); // status to save from the amount step
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [promiseDate, setPromiseDate] = useState("");
  const [saving, setSaving] = useState(false);

  const loadLoan = useCallback(() => {
    if (!code) return;
    api.get(`/loans/${code}`).then((r) => setLoan(r.data.loan)).catch(() => {});
  }, [code]);
  useEffect(() => { loadLoan(); }, [loadLoan]);

  const goBack = () => navigate(`/collection?tab=${fromTab}`);

  const submit = async (status, extra = {}) => {
    setSaving(true);
    try {
      await api.post("/collections/followup", {
        loanCode: code,
        status,
        note: extra.note ?? note,
        amountCollected: extra.amountCollected ?? 0,
        promiseDate: extra.promiseDate ?? null,
      });
      toast.success(`${code} → ${status}`);
      goBack();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
      setSaving(false);
    }
  };

  const StepShell = ({ title, children }) => (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 max-w-lg" data-testid="collection-action-card">
      <h3 className="font-heading font-semibold text-slate-900 mb-4">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );

  return (
    <Layout>
      <button type="button" onClick={goBack} data-testid="action-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Collection Workflow
      </button>

      <PageHeader title="Collection Action" subtitle="Step-by-step follow-up" />

      {loan && (
        <div className="bg-slate-900 text-white rounded-lg px-5 py-4 mb-6 flex items-center justify-between gap-4 flex-wrap max-w-lg">
          <div>
            <Link to={`/customer?q=${encodeURIComponent(loan.customerName)}`} className="font-heading font-bold text-lg hover:underline">{loan.customerName}</Link>
            <div className="text-xs text-slate-300 font-mono-fig">{loan.code} · due day {loan.dueDay || "—"}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Outstanding</div>
            <div className="font-mono-fig text-xl font-bold">{fmtRM(loan.outstanding)}</div>
          </div>
        </div>
      )}

      {saving && (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Saving…</div>
      )}

      {!saving && step === "payment" && (
        <StepShell title="Is the payment collected?">
          <Choice testid="action-collected-yes" icon={CheckCircle2} tone="green" label="Yes — payment collected" hint="Marks this overdue as completed" onClick={() => { setAmountStatus("Collected"); setStep("amount"); }} />
          <Choice testid="action-collected-no" icon={Ban} label="No" hint="Continue to contact follow-up" onClick={() => setStep("contact")} />
        </StepShell>
      )}

      {!saving && step === "amount" && (
        <StepShell title={amountStatus === "Partial" ? "How much partial payment was made?" : "How much was collected?"}>
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Amount collected (RM)</label>
            <div className="relative mt-1.5">
              <HandCoins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="number" step="0.01" data-testid="action-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
            </div>
          </div>
          <textarea data-testid="action-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
          <button data-testid="action-confirm-collected" onClick={() => submit(amountStatus, { amountCollected: parseFloat(amount) || 0 })}
            className={`w-full text-white rounded-md px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 ${amountStatus === "Partial" ? "bg-orange-500 hover:bg-orange-600" : "bg-green-600 hover:bg-green-700"}`}>
            <CheckCircle2 className="h-4 w-4" /> {amountStatus === "Partial" ? "Save Partial Payment" : "Mark Collected — Completed"}
          </button>
          <button onClick={() => setStep(amountStatus === "Partial" ? "outcome" : "payment")} className="w-full text-slate-500 text-sm hover:text-slate-900">Back</button>
        </StepShell>
      )}

      {!saving && step === "contact" && (
        <StepShell title="Is the customer contacted?">
          <Choice testid="action-no-answer" icon={PhoneMissed} tone="amber" label="Call — no answer" onClick={() => submit("No Answer")} />
          <Choice testid="action-cancel-call" icon={PhoneOff} tone="amber" label="Call cancelled / declined" onClick={() => submit("Cancelled Call")} />
          <Choice testid="action-invalid-number" icon={Ban} tone="red" label="Number invalid" onClick={() => submit("Invalid Number")} />
          <Choice testid="action-contacted-yes" icon={Phone} tone="green" label="Yes — customer contacted" hint="What did they say?" onClick={() => setStep("outcome")} />
          <button onClick={() => setStep("payment")} className="w-full text-slate-500 text-sm hover:text-slate-900">Back</button>
        </StepShell>
      )}

      {!saving && step === "outcome" && (
        <StepShell title="What did the customer say?">
          <Choice testid="action-promised" icon={CheckCircle2} tone="default" label="Promised to pay" hint="Set a promise date" onClick={() => setStep("promiseDate")} />
          <Choice testid="action-partial" icon={HandCoins} tone="amber" label="Made a partial payment" onClick={() => { setAmountStatus("Partial"); setStep("amount"); }} />
          <Choice testid="action-refused" icon={Ban} tone="red" label="Refused to pay" onClick={() => submit("Refused")} />
          <Choice testid="action-contacted-other" icon={Phone} label="Other — just contacted" onClick={() => submit("Contacted")} />
          <button onClick={() => setStep("contact")} className="w-full text-slate-500 text-sm hover:text-slate-900">Back</button>
        </StepShell>
      )}

      {!saving && step === "promiseDate" && (
        <StepShell title="When did they promise to pay?">
          <input type="date" data-testid="action-promise-date" value={promiseDate} onChange={(e) => setPromiseDate(e.target.value)}
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
          <textarea data-testid="action-promise-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
          <button data-testid="action-confirm-promise" onClick={() => submit("Promised to Pay", { note, promiseDate })}
            className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2.5 text-sm font-medium">Save Promise to Pay</button>
          <button onClick={() => setStep("outcome")} className="w-full text-slate-500 text-sm hover:text-slate-900">Back</button>
        </StepShell>
      )}
    </Layout>
  );
}
